<template>
    <div class="w-full relative flex flex-wrap justify-between rounded border border-gray-800 mb-2"
         :class="[ rowColor, depth > 0 ? 'border-r-0 rounded-r-none' : '' ]">

      <!-- drag handle for reordering rows -->
      <RowDragHandle v-if="mode === modes.DEFAULT"></RowDragHandle>

      <!-- debug -->
      <div v-if="settings.debug.showJsonModels" class="text-xs">
        <p v-if="model.id">id: {{ model.id }}</p>
        <p v-if="model.root_id">root_id: {{ model.root_id}}</p>
        <p v-if="model.parent_id">parent_id: {{model.parent_id}}</p>
        <p v-if="model.link_id">link_id {{ model.link_id }}</p>
        <p v-if="model.price_id">price_id {{ model.price_id }}</p>
      </div>

      <!-- row number and child toggle button -->
      <div class="flex-1 lg:flex-initial flex mb-2 lg:mb-0 mr-2 pl-2 relative">
        <RowNumber  v-if="mode !== modes.REFERENCE"
                    :number="number"></RowNumber>
        <div class="inline-block w-4 h-4 mr-1 my-2">
          <RowChildToggle v-model="showChildren"></RowChildToggle>
        </div>
      </div>

      <div  v-if="mode !== modes.DEFAULT"
            class="my-1">
        <SortButton class="mr-2"
                    @click="$root.$emit('add-row', model)">
          <IconAdd></IconAdd>
        </SortButton>
      </div>
      <div  v-else
            class="flex my-1">
        <RowAddMenu :disabled="editable"
                    :showReferenceButton="true"
                    @addParentRowBelow="addParentRowBelow"
                    @addParentRowChild="addParentRowChild"
                    @addBlankRowBelow="addNewRowBelow"
                    @addBlankRowChild="addNewRowChild"
                    @importRowBelow="importRowBelow"
                    @importRowChild="importRowChild"
                    @addLinkedRowBelow="referenceRowBelow"
                    @addLinkedRowChild="referenceRowChild"></RowAddMenu>
        <RowContextMenu :isReference="isReference"
                        :source="model.source"
                        :disabled="editable"
                        :active="isActive"
                        :color="model.color"
                        @color-selected="colorSelected"
                        @convertToIndependent="$root.$emit('convert-to-independent', model)"
                        @duplicateRow="duplicateRow"
                        @deleteRow="deleteThisRow"
                        @toggle-active="toggleRowActive"></RowContextMenu>
      </div>

      <div class="w-full lg:w-auto mx-2 lg:mx-0 flex lg:flex-1 mb-2 lg:my-1 border border-gray-800 rounded bg-white">
        <RowLinkedDescription v-if="isReference"
                              v-model="reference_model.linked_description"
                              :editable="editable"
                              :mode="mode"
                              :disabled="mode !== modes.DEFAULT"
                              :active="isActive"
                              @edit-reference="$root.$emit('edit-reference', model)"></RowLinkedDescription>

        <RowDescription v-model="description"
                        :isParent="true"
                        :isReference="isReference"
                        :mode="mode"
                        :active="isActive"
                        :disabled="mode !== modes.DEFAULT || editable"></RowDescription>


        <div  class="w-8 h-full p-1 rounded-r bg-gray-200 border-l border-gray-800">
          <IconBlocks></IconBlocks>
        </div>

      </div>

      <div  class="w-full lg:w-72 mx-2 mb-2 lg:my-1 ml-2 flex border border-gray-800 rounded">
        <RowSummary :model="model"
                    :price="price"
                    :active="isActive"
                    :disabled="mode !== modes.DEFAULT || editable"
                    :settings="settings"></RowSummary>
      </div>

    <!-- disabled overlay -->
    <transition name="fade">
      <div v-if="disabled" class="w-full h-full absolute left-0 top-0 bg-gray-100 opacity-90 rounded-sm"></div>
    </transition>

    <!-- Start: Children -->
    <draggable class="pl-4 lg:pl-2 w-full rounded-sm"
               handle=".handle"
               v-if="showChildren"
               v-model="model.rows">

      <Row v-for="(row, index) in model.rows"
           :model="row"
           :parent="model"
           :mode="mode"
           :rootRow="rootRow"
           :key="index"
           :index="index"
           :depth="depth + 1"
           :number="number"
           :disabled="rowDisabled(row)"
           :editable="rowIsBeingEdited(row)"></Row>

    </draggable>
    <!-- End: Children -->
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import { modes } from '@/constants'

import SortButton from '@/components/ui/SortButton'
import IconAdd from '@/components/icons/IconAdd'
import IconBlocks from '@/components/icons/IconBlocks'

import RowNumber from '@/components/row/RowNumber'
import RowChildToggle from '@/components/row/RowChildToggle'
import RowDragHandle from '@/components/row/RowDragHandle'
import RowAddMenu from '@/components/row/RowAddMenu'
import RowDescription from '@/components/row/description/RowDescription'
import RowLinkedDescription from '@/components/row/description/RowLinkedDescription'
import RowSummary from '@/components/row/RowSummary'
import RowContextMenu from '@/components/row/RowContextMenu'

import { flatten } from '@/helpers'
import { getRowColor } from '@/row'

export default {
  name: 'RowParent',
  components: {
    draggable,
    SortButton,
    IconAdd,
    IconBlocks,
    RowNumber,
    RowChildToggle,
    RowDragHandle,
    RowAddMenu,
    RowDescription,
    RowLinkedDescription,
    RowSummary,
    RowContextMenu
  },
  props: {
    model: {
      type: Object,
      default() {
        return {}
      }
    },
    parent: {
      type: Object,
      default() {
        return {}
      }
    },
    rootRow: {
      type: Object,
      default() {
        return {}
      }
    },
    depth: {
      type: Number,
      default: 0
    },
    index: {
      type: Number,
      default: 0
    },
    number: {
      type: String,
      default: '1'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    },
    mode: {
      type: String,
      default: 'DEFAULT'
    }
  },
  data() {
    return {
      modes,
      showChildren: true
    };
  },
  computed: {
    ...mapGetters('prices', {
      price: 'price',
    }),
    ...mapGetters('settings', {
      settings: 'settings'
    }),
    rowColor(){
      return getRowColor(this.model, this.depth, this.parent)
    },
    isReference() {
      return this.model.is_reference// && this.mode !== 'ADD' // !== undefined ? true : false;
    },
    isActive(){
      if(this.mode === modes.DEFAULT){
        return this.model.active
      }else{
        return true
      }
    },
    description: {
      get() {
        return this.model.is_reference && this.mode !== modes.DEFAULT ?  this.model.linked_description : this.model.description
      },
      set(newValue) {
        if(this.model.is_reference && this.mode !== modes.DEFAULT){
          //this.model.linked_description = newValue
        }else{
          this.model.description = newValue
        }
      }
    },
    reference_model(){
      let price = this.$store.state.prices.prices.filter(p => p.id === this.model.price_id)[0]
      //console.log(this.$store.state.prices.prices.filter(p => p.id === this.model.price_id))
      let rows = flatten(price.rows)

      return rows.filter((row) => {
        return row.id === this.model.link_id;
      })[0]
    }
  },
  methods: {
    colorSelected(color){
      let payload = {
        row: this.model,
        color: color
      }
      this.$store.dispatch('prices/setRowColor', payload)
    },
    toggleRowActive(){
      if(!Object.prototype.hasOwnProperty.call(this.model, 'active')){
        Vue.set(this.model, 'active', true)
      }
      this.model.active = !this.model.active
      this.setChildrenActive(this.model.rows)
    },
    setChildrenActive(rows){
      rows.forEach(row => {
        row.active = this.model.active
        if(row.rows && row.rows.length > 0){
          this.setChildrenActive(row.rows)
        }
      })
    },
    rowDisabled(row){
      if(this.$store.getters['prices/editingReference'] !== ''){
        if(row.id === this.$store.getters['prices/editingReference'] || row.link_id === this.$store.getters['prices/editingReference']){
          return false
        }
        return true
      }else{
        return false
      }
    },
    rowIsBeingEdited(row){
      if(row.is_reference){
        if(this.$store.getters['prices/editingReference'] !== ''){
          if(row.id === this.$store.getters['prices/editingReference'] ||
          row.link_id === this.$store.getters['prices/editingReference']){
            return true
          }
        }
      }
      return false
    },
    addParentRowBelow(){
      let model = this.parent
      let index = this.index + 1
      this.$store.dispatch('prices/addParentRow', { model, index })
    },
    addParentRowChild(){
      let model = this.model
      let index = this.index + 1

      this.$store.dispatch('prices/addParentToParentModelAtIndex', { model, index })
    },
    addNewRowBelow() {
      //this.$root.$emit('add-new-row-below', this.parent, this.index + 1)
      //this.$emit('add-new-row-below', this.index + 1)
      //add the row to our parent so it appears below this one
      let model = this.parent
      let index = this.index + 1
      this.$store.dispatch('prices/addRowToModelAtIndex', { model, index })
    },
    addNewRowChild() {
      console.log('addBlankRowChild')

      let model = this.model
      let index = this.index + 1
      //this.$store.dispatch('prices/addChildToModelAtIndex', { model, index })

      this.$store.dispatch('prices/addChildToParentModelAtIndex', { model, index })
    },
    importRowBelow(){
      console.log('importRowBelow ROW');
      this.$root.$emit('show-import-modal', this.parent, this.index + 1 )
    },
    importRowChild(){
      console.log('importRowChild ROW');
      this.$root.$emit('show-import-modal', this.model, this.index + 1 )
    },
    referenceRowBelow() {
      console.log('addLinkedRowBelow ROW');
      this.$root.$emit('show-reference-modal', this.parent, this.index + 1 )
    },
    referenceRowChild() {
      console.log('addLinkedRowChild');
      let index = this.model.rows ? this.model.rows.length : 0
      this.$root.$emit('show-reference-modal', this.model, index)
    },
    deleteThisRow() {
      if (window.confirm('Are you sure you want to delete this row?')) {
        this.model.depth = this.depth;
        this.model.index = this.index;

        let row = this.model
        let model = this.parent
        this.$store.dispatch('prices/deleteRowFromModel', { row, model })
      }
    },
    duplicateRow() {
      let row = this.model
      let model = this.parent
      let index = this.index + 1
      this.$store.dispatch('prices/duplicateRowInModelAtIndex', { row, model, index })
    }
  }
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity .25s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>